class GameCollection {
  constructor() {
    console.log('GameCollection: Constructor initialized');
    this.currentProducts = [];
    this.currentFilters = {
      availability: 'all',
      priceRange: 'all'
    };
    this.currentSort = 'featured';
    this.currentCategory = null;
    this.currentGame = null;
    this.isGameCollection = false;
    this.init();
  }

  init() {
    console.log('GameCollection: Initializing...');
    this.setupEventListeners();
    this.parseUrlAndLoad();
  }

  parseUrlAndLoad() {
    const path = window.location.pathname;
    console.log('GameCollection: Current path:', path);
    const pathParts = path.split('/').filter(part => part);

    const collectionsIndex = pathParts.indexOf('collections');
    if (collectionsIndex === -1) {
      console.error('GameCollection: Not on a collections page');
      return;
    }

    const collectionPart = pathParts[collectionsIndex + 1] || null;
    if (!collectionPart) return;

    if (collectionPart === 'all') {
      this.isGameCollection = false;
      this.currentCategory = 'all';
      this.currentGame = null;
    } else if (collectionPart.endsWith('-merch')) {
      this.isGameCollection = true;
      this.currentCategory = null;
      this.currentGame = collectionPart.replace('-merch', '');
    } else {
      this.isGameCollection = false;
      this.currentCategory = collectionPart;
      this.currentGame = pathParts[collectionsIndex + 2] || null;
    }

    console.log('GameCollection: Parsed URL -', {
      category: this.currentCategory,
      game: this.currentGame,
      isGameCollection: this.isGameCollection
    });

    this.loadGame(this.currentGame);
    this.updateHeaderText();
  }

  getGameCollectionHandle(game) {
    if (!game) return null;
    return game.endsWith('-merch') ? game : `${game}-merch`;
  }

  async loadGame(gameCollection = null) {
    console.log('GameCollection: Loading collection -', {
      category: this.currentCategory,
      game: gameCollection,
      isGameCollection: this.isGameCollection
    });

    const gridContainer = document.querySelector('.product-grid');
    if (!gridContainer) {
      console.error('GameCollection: Product grid container not found');
      return;
    }

    gridContainer.innerHTML = '<div class="loading">Loading products...</div>';

    try {
      let url;

      if (this.isGameCollection || gameCollection) {
        const gameHandle = this.getGameCollectionHandle(gameCollection || this.currentGame);
        url = `/collections/${gameHandle}/products.json?limit=250`;
      } else if (this.currentCategory === 'all') {
        url = '/collections/all/products.json?limit=250';
      } else {
        url = `/collections/${this.currentCategory}/products.json?limit=250`;
      }

      console.log('GameCollection: Fetching products from:', url);

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch products');

      const data = await response.json();

      if (!this.isGameCollection && this.currentCategory !== 'all' && gameCollection) {
        const categoryTag = this.currentCategory.toLowerCase();
        this.currentProducts = data.products.filter(product =>
          product.tags.some(tag => tag.toLowerCase() === categoryTag)
        );
      } else {
        this.currentProducts = data.products;
      }

      this.applyFiltersAndSort();
      this.setActiveGameButton(gameCollection);

      let newPath;
      if (this.isGameCollection) {
        newPath = gameCollection
          ? `/collections/${this.getGameCollectionHandle(gameCollection)}`
          : '/collections/all';
      } else if (this.currentCategory === 'all') {
        newPath = gameCollection
          ? `/collections/${this.getGameCollectionHandle(gameCollection)}`
          : '/collections/all';
      } else {
        newPath = gameCollection
          ? `/collections/${this.currentCategory}/${gameCollection}`
          : `/collections/${this.currentCategory}`;
      }

      history.pushState({}, '', newPath);
      this.updateHeaderText();
    } catch (error) {
      console.error('GameCollection: Error loading products:', error);
      gridContainer.innerHTML = '<div class="loading">Error loading products. Please try again.</div>';
    }
  }

  applyFiltersAndSort() {
    console.log('GameCollection: Applying filters and sort:', {
      filters: this.currentFilters,
      sort: this.currentSort
    });

    let filteredProducts = [...this.currentProducts];

    if (this.currentFilters.availability === 'in-stock') {
      filteredProducts = filteredProducts.filter(product => product.available);
    } else if (this.currentFilters.availability === 'out-of-stock') {
      filteredProducts = filteredProducts.filter(product => !product.available);
    }

    if (this.currentFilters.priceRange !== 'all') {
      const [min, max] = this.currentFilters.priceRange.split('-').map(Number);
      filteredProducts = filteredProducts.filter(product => {
        const price = parseFloat(product.variants[0].price);
        return price >= min && (max ? price <= max : true);
      });
    }

    switch (this.currentSort) {
      case 'price-ascending':
        filteredProducts.sort((a, b) => parseFloat(a.variants[0].price) - parseFloat(b.variants[0].price));
        break;
      case 'price-descending':
        filteredProducts.sort((a, b) => parseFloat(b.variants[0].price) - parseFloat(a.variants[0].price));
        break;
      case 'title-ascending':
        filteredProducts.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-descending':
        filteredProducts.sort((a, b) => b.title.localeCompare(a.title));
        break;
    }

    console.log('GameCollection: Products after filtering:', filteredProducts.length);
    this.renderProducts(filteredProducts);
  }

  getActiveCurrency() {
    if (window.Shopify?.currency?.active) return Shopify.currency.active;
    const meta = document.querySelector('meta[name="shop-currency"]');
    return meta ? meta.content : 'USD';
  }

  formatPrice(raw) {
    const amount = parseFloat(raw);
    const currency = this.getActiveCurrency();

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 2
    }).format(amount);
  }

  renderProducts(products) {
    console.log('GameCollection: Rendering products:', products.length);

    const gridContainer = document.querySelector('.product-grid');
    if (!gridContainer) {
      console.error('GameCollection: Product grid container not found during render');
      return;
    }

    if (products.length === 0) {
      gridContainer.innerHTML = '<div class="loading">No products found.</div>';
      return;
    }

    gridContainer.innerHTML = products.map(product => `
      <div class="product-card">
        <a href="/products/${product.handle}">
          <img src="${product.images[0]?.src || ''}" alt="${product.title}">
          <h3>${product.title}</h3>
          <p class="price">${this.formatPrice(product.variants[0].price)}</p>
          ${product.variants.every(variant => !variant.available) ? '<div class="sold-out"><span>Sold Out</span></div>' : ''}
        </a>
      </div>
    `).join('');
  }

  setActiveGameButton(collectionHandle) {
    console.log('GameCollection: Setting active game button:', collectionHandle);
    document.querySelectorAll('.game-selector button').forEach(button => {
      const buttonGame = button.dataset.game;
      const isActive = buttonGame === collectionHandle || (!collectionHandle && !buttonGame);
      button.classList.toggle('active', isActive);
    });
  }

  setupEventListeners() {
    console.log('GameCollection: Setting up event listeners');

    document.querySelectorAll('.game-selector button').forEach(button => {
      button.addEventListener('click', () => {
        const gameCollection = button.dataset.game || null;
        console.log('GameCollection: Game button clicked:', {
          gameCollection,
          isGameCollection: this.isGameCollection
        });

        if (!gameCollection && this.isGameCollection) {
          this.currentGame = null;
          this.currentCategory = 'all';
          this.isGameCollection = false;
          window.location.href = '/collections/all';
          return;
        }

        if (this.isGameCollection) {
          this.currentGame = gameCollection;
          window.location.href = `/collections/${this.getGameCollectionHandle(gameCollection)}`;
          return;
        }

        document.querySelectorAll('.game-selector button').forEach(btn =>
          btn.classList.remove('active')
        );
        button.classList.add('active');

        this.currentGame = gameCollection;

        if (this.currentCategory === 'all' && gameCollection) {
          this.isGameCollection = true;
          this.currentCategory = null;
        }

        this.loadGame(gameCollection);
      });
    });

    document.querySelector('.availability-filter')?.addEventListener('change', (e) => {
      this.currentFilters.availability = e.target.value;
      this.applyFiltersAndSort();
    });

    document.querySelector('.price-filter')?.addEventListener('change', (e) => {
      this.currentFilters.priceRange = e.target.value;
      this.applyFiltersAndSort();
    });

    document.querySelector('.sort-selector')?.addEventListener('change', (e) => {
      this.currentSort = e.target.value;
      this.applyFiltersAndSort();
    });

    window.addEventListener('popstate', () => {
      this.parseUrlAndLoad();
    });
  }

  updateHeaderText() {
    const headerElement = document.querySelector('.collection-header h1, h2.text-center');
    if (!headerElement) return;

    let headerText;
    if (this.isGameCollection) {
      const gameName = this.currentGame
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      headerText = `Shop ${gameName}`;
    } else if (this.currentCategory === 'all') {
      headerText = 'Shop All Merch';
    } else {
      const categoryName = this.currentCategory
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (this.currentGame) {
        const gameName = this.currentGame
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        headerText = `Shop ${gameName} ${categoryName}`;
      } else {
        headerText = `Shop All ${categoryName}`;
      }
    }

    headerElement.textContent = headerText;
  }
}

console.log('GameCollection: Script loaded, waiting for DOM ready');
document.addEventListener('DOMContentLoaded', () => {
  console.log('GameCollection: DOM ready, initializing');
  window.gameCollection = new GameCollection();
});